/** Maximum number of decimal places accepted in the amount input. */
export var MAX_AMOUNT_DECIMALS = 6;
/** Minimum amount accepted by the anchor. */
export var MIN_AMOUNT = 0.11;
var NON_ASCII_DECIMAL_SEPARATOR_RE = /[\u066b\u3002\uff0e]/g;
var GROUPED_THOUSANDS_RE = /^(?!0,)\d{1,3}(?:,\d{3})+(?:\.\d*)?$/;
var GROUPED_THOUSANDS_WITH_COMMA_DECIMAL_RE = /^((?!0,)\d{1,3}(?:,\d{3})+),(\d*)$/;
/**
 * Normalize decimal separators emitted by mobile keyboards while preserving
 * comma grouping generated by this UI.
 */
export var normalizeAmountInput = function (value) {
    var normalized = String(value).replace(NON_ASCII_DECIMAL_SEPARATOR_RE, ".");
    if (!normalized.includes(".") && normalized.includes(",")) {
        var groupedCommaDecimal = normalized.match(GROUPED_THOUSANDS_WITH_COMMA_DECIMAL_RE);
        if (groupedCommaDecimal) {
            normalized = "".concat(groupedCommaDecimal[1], ".").concat(groupedCommaDecimal[2]);
        }
        else {
            normalized = GROUPED_THOUSANDS_RE.test(normalized)
                ? normalized
                : normalized.replace(",", ".");
        }
    }
    return normalized;
};
/**
 * Strip display formatting (thousands separators, and a dangling decimal point
 * left mid-typing) so the exact decimal string the user typed can be sent to
 * the backend untouched. Digits are never added or removed.
 */
export var parseAmount = function (value) {
    return normalizeAmountInput(value).replace(/,/g, "").replace(/\.$/, "");
};
/**
 * Trim insignificant decimal zeroes for display without a float round-trip.
 */
export var trimInsignificantAmountZeros = function (value) {
    var raw = normalizeAmountInput(value).trim();
    if (!raw || !/^\d*\.?\d*$/.test(raw) || raw === ".")
        return value;
    var _a = raw.split("."), intPart = _a[0], decPart = _a[1];
    if (decPart === undefined)
        return raw;
    var displayDecimals = decPart.replace(/0+$/, "");
    return displayDecimals
        ? "".concat(intPart || "0", ".").concat(displayDecimals)
        : intPart || "0";
};
/**
 * Format for display: keep the user's decimals verbatim, only group the integer
 * part with commas. Never rounds and never drops digits.
 */
export var formatAmountInput = function (value) {
    var _a;
    var normalized = normalizeAmountInput(value);
    // Remove everything that is not a digit or a decimal point.
    var cleaned = normalized.replace(/[^\d.]/g, "");
    // Collapse extra decimal points into a single one.
    var parts = cleaned.split(".");
    var intPart = (_a = parts[0]) !== null && _a !== void 0 ? _a : "";
    var decPart = parts.length > 1 ? parts.slice(1).join("") : undefined;
    var groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decPart === undefined ? groupedInt : "".concat(groupedInt, ".").concat(decPart);
};
/** Number of decimal places in a raw (comma-free) decimal string. */
export var countDecimals = function (rawValue) {
    var decPart = rawValue.split(".")[1];
    return decPart ? decPart.length : 0;
};
/**
 * Validate an amount input. Returns an error message, or undefined when valid.
 * Works on the string so a value like "0.123456" is never coerced through a
 * float that could lose or invent digits.
 */
export var validateAmount = function (displayValue, rules) {
    var _a;
    if (rules === void 0) { rules = {}; }
    var raw = parseAmount(displayValue).trim();
    var maxDecimals = (_a = rules.maxDecimals) !== null && _a !== void 0 ? _a : MAX_AMOUNT_DECIMALS;
    if (!raw) {
        return "Amount is required";
    }
    if (!/^\d*\.?\d*$/.test(raw) || raw === ".") {
        return "Enter a valid amount";
    }
    if (countDecimals(raw) > maxDecimals) {
        return "Amount supports at most ".concat(maxDecimals, " decimal places");
    }
    var parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < MIN_AMOUNT) {
        return "Amount must be more than 0.1";
    }
    if (rules.maxAmount !== undefined && parsed > rules.maxAmount) {
        return "Amount cannot exceed ".concat(rules.maxAmount.toLocaleString("en-US"), " per transaction");
    }
    return undefined;
};
